import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiHeart, FiShoppingBag } from "react-icons/fi";
import "../css/main.scss";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

const categoryTiles = [
  ["Coloured pencils", "Art Supplies", "Coloured Pencils", "/img/06-coloured-pencil-set-v2.png"],
  ["Watercolour", "Art Supplies", "Watercolour Paint", "/img/07-portable-watercolour-v2.png"],
  ["Acrylic colour", "Art Supplies", "Acrylic Paint", "/img/08-acrylic-paint-set-v2.png"],
  ["Gouache", "Art Supplies", "Gouache", "/img/09-matte-gouache-set-v2.png"],
  ["Brushes", "Art Supplies", "Brushes", "/img/10-brush-roll-v2.png"],
  ["Sketchbooks", "Paper & Pads", "Sketchbooks", "/img/11-cloth-sketchbook-v2.png"],
  ["Watercolour pads", "Paper & Pads", "Watercolour Pads", "/img/12-watercolour-pad-v2.png"],
  ["Notebooks", "Paper & Pads", "Notebooks & Journals", "/img/13-dot-grid-journal-v2.png"],
  ["Calligraphy", "Hobby & Creativity", "Calligraphy", "/img/14-calligraphy-kit-v2.png"],
  ["Kids' pens", "Kids", "Kids' Pens", "/img/18-jumbo-wax-crayons-v2.png"],
];

const editorialTiles = [
  { title: "Every line starts somewhere", label: "Writing tools", image: "/img/15-fountain-pen-ink-v2.png", category: "Pens", tone: "yellow" },
  { title: "Start with bold marks", label: "Drawing tools", image: "/img/16-willow-charcoal-v2.png", category: "Art Supplies", tone: "blue" },
  { title: "Make colour move", label: "Soft colour", image: "/img/17-soft-pastel-box-v2.png", category: "Art Supplies", tone: "coral" },
  { title: "Keep tools close", label: "Desk storage", image: "/img/19-canvas-pencil-pouch-v2.png", category: "Hobby & Creativity", tone: "mint" },
];

function ProductCard({ product }) {
  const { addToCart, toggleWishlist, wishlist, openCart } = useContext(CartContext);
  const wished = wishlist.includes(product.id);
  const add = () => {
    addToCart(product.id, 1, product.variants[0], product.colors[0]);
    openCart();
  };

  return (
    <article className="home-product-card">
      <Link to={`/shopDetails/${product.id}`} className="home-product-card__image">
        {product.isNew && <span>New</span>}
        <img src={product.image} alt={product.name} loading="lazy" width="1254" height="1254" />
      </Link>
      <button
        type="button"
        className={`home-product-card__wish ${wished ? "is-active" : ""}`}
        onClick={() => toggleWishlist(product.id)}
        aria-label={`${wished ? "Remove" : "Add"} ${product.name} ${wished ? "from" : "to"} wishlist`}
        aria-pressed={wished}
      >
        <FiHeart />
      </button>
      <div className="home-product-card__info">
        <span>{product.brand}</span>
        <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
        <strong>{formatPrice(product.price)}</strong>
      </div>
      <button type="button" className="home-product-card__add" onClick={add}>
        <FiShoppingBag /> Quick add
      </button>
    </article>
  );
}

function Main() {
  const navigate = useNavigate();
  const featured = productData.filter((product) => product.featured).slice(0, 5);
  const newProducts = productData.filter((product) => product.isNew).slice(0, 5);

  const goTo = ({ category, subcategory, newOnly }) => {
    const query = new URLSearchParams();
    if (newOnly) query.set("new", "true");
    if (category) query.set("category", category);
    if (subcategory) query.set("subcategory", subcategory);
    navigate(`/shop?${query.toString()}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="main home-page">
      <main>
        <section className="home-page__quick" aria-label="Quick shop links">
          <Link to="/shop?price=45-80">$45–$80</Link>
          <Link to="/shop?new=true">New products</Link>
          <Link to="/shop">All products</Link>
          <Link to="/shop?sort=rating">Top rated</Link>
        </section>

        <section className="home-page__hero">
          <img src="/main/hero-poodliving.png" alt="An overhead spread of colourful creative tools on a home studio desk" width="1536" height="1024" />
          <div className="home-page__hero-copy">
            <p>Tools for curious minds</p>
            <h1>Make room<br />for creativity.</h1>
            <Link to="/shop">Shop the collection <FiArrowRight /></Link>
          </div>
        </section>

        <section className="home-page__categories" aria-labelledby="categories-title">
          <h2 id="categories-title" className="sr-only">Popular categories</h2>
          <div>
            {categoryTiles.map(([label, category, subcategory, image]) => (
              <button type="button" key={label} onClick={() => goTo({ category, subcategory })}>
                <span><img src={image} alt="" loading="lazy" /></span>
                <b>{label}</b>
              </button>
            ))}
          </div>
        </section>

        <nav className="home-page__link-strip" aria-label="Explore the shop">
          <Link to="/shop?new=true">New arrivals</Link>
          <Link to="/shop?category=Art%20Supplies">Art supplies</Link>
          <Link to="/shop?subcategory=Brushes">Brushes</Link>
          <Link to="/shop?subcategory=Watercolour%20Paint">Watercolour paint</Link>
          <Link to="/shop?category=Pens">Gift pens</Link>
          <Link to="/about">Our story</Link>
        </nav>

        <section className="home-page__products" aria-labelledby="staff-picks-title">
          <div className="home-page__section-title">
            <div><p>Chosen by people who make things</p><h2 id="staff-picks-title">Staff picks</h2></div>
            <Link to="/shop?sort=rating">View all <FiArrowRight /></Link>
          </div>
          <div className="home-page__product-grid">
            {featured.map((product) => <ProductCard product={product} key={product.id} />)}
          </div>
        </section>

        <section className="home-page__editorial" aria-label="Explore collections">
          {editorialTiles.map((tile) => (
            <button type="button" className={`home-page__editorial-tile home-page__editorial-tile--${tile.tone}`} key={tile.title} onClick={() => goTo(tile)}>
              <img src={tile.image} alt="" loading="lazy" />
              <span><small>{tile.label}</small><b>{tile.title}</b><i>Explore <FiArrowRight /></i></span>
            </button>
          ))}
        </section>

        <section className="home-page__mosaic" aria-label="Creative essentials">
          <button type="button" className="home-page__mosaic-wide home-page__mosaic-paint" onClick={() => goTo({ category: "Art Supplies" })}>
            <span><small>Build your palette</small><b>Colour is a language.</b><i>Shop artist colour <FiArrowRight /></i></span>
            <img src="/main/mosaic-colour.png" alt="Paint brushes, mixing palette and colour studies" loading="lazy" />
          </button>
          <ProductCard product={productData[19]} />
          <button type="button" className="home-page__mosaic-story" onClick={() => navigate("/about")}>
            <img src="/main/mosaic-studio.png" alt="A carefully organised home studio shelf" loading="lazy" />
            <span><small>Inside our studio</small><b>Tools chosen with care.</b></span>
          </button>
          <button type="button" className="home-page__mosaic-half home-page__mosaic-paper" onClick={() => goTo({ category: "Paper & Pads" })}>
            <img src="/main/mosaic-paper.png" alt="A tactile collection of notebooks and artist papers" loading="lazy" />
            <span><b>Paper worth keeping</b><i>Explore paper & pads <FiArrowRight /></i></span>
          </button>
          <button type="button" className="home-page__mosaic-half home-page__mosaic-kids" onClick={() => goTo({ category: "Kids" })}>
            <img src="/main/mosaic-kids.png" alt="A bright creative table prepared for children" loading="lazy" />
            <span><b>Big colour for little makers</b><i>Shop kids <FiArrowRight /></i></span>
          </button>
        </section>

        <section className="home-page__products" aria-labelledby="new-arrivals-title">
          <div className="home-page__section-title">
            <div><p>Just unpacked</p><h2 id="new-arrivals-title">New arrivals</h2></div>
            <Link to="/shop?new=true">See what’s new <FiArrowRight /></Link>
          </div>
          <div className="home-page__product-grid">
            {newProducts.map((product) => <ProductCard product={product} key={product.id} />)}
          </div>
        </section>

        <section className="home-page__inspiration" aria-labelledby="inspiration-title">
          <div className="home-page__section-title"><h2 id="inspiration-title">Inspiration breeds creativity</h2><Link to="/shop">All tools <FiArrowRight /></Link></div>
          <div>
            <article><img src="/main/inspire-calligraphy.png" alt="A hand practising abstract calligraphy strokes" loading="lazy" /><span><small>Start here</small><h3>A slower way to write</h3></span></article>
            <article><img src="/main/inspire-watercolour.png" alt="A small watercolour setup beside the harbour" loading="lazy" /><span><small>Field guide</small><h3>Painting with a tiny palette</h3></span></article>
            <article><img src="/main/inspire-charcoal.png" alt="Loose charcoal practice marks on textured paper" loading="lazy" /><span><small>Technique</small><h3>Why charcoal loves a loose hand</h3></span></article>
            <article><img src="/main/inspire-journal.png" alt="An evening journaling setup by a city window" loading="lazy" /><span><small>Ideas</small><h3>Make a journal you return to</h3></span></article>
          </div>
        </section>

        <section className="home-page__manifesto">
          <p>{siteConfig.brandName}</p>
          <h2>{siteConfig.tagline}</h2>
          <div>
            <p>{siteConfig.description} We believe good materials should feel inviting, work reliably and leave the interesting decisions to you.</p>
            <p>Our collection brings together everyday pens, artist colour, paper and small studio companions at clear prices. Each item is described with practical specifications so it is easier to choose the right tool for the way you work.</p>
          </div>
          <Link to="/about">Read our story <FiArrowRight /></Link>
        </section>
      </main>
    </div>
  );
}

export default Main;
